import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../firebaseConfig";

const previewCall = httpsCallable(functions, "getBillingPeriodPreview", { timeout: 95000 });
const startCall = httpsCallable(functions, "recordMcBillingCountStart", { timeout: 30000 });
const requests = new Map();
const captures = new Map();
const queueKey = (orgId, uid) => `matrixos.billing.pendingStarts.${orgId}.${uid}`;

export function currentBillingMonth(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Africa/Johannesburg", year: "numeric", month: "2-digit" }).formatToParts(now);
  return `${parts.find((p) => p.type === "year").value}-${parts.find((p) => p.type === "month").value}`;
}
export function billingMonthOptions(now = new Date()) {
  const [year, month] = currentBillingMonth(now).split("-").map(Number);
  return Array.from({ length: 37 }, (_, offset) => {
    const date = new Date(Date.UTC(year, month - 1 - offset, 1, 12));
    return { value: date.toISOString().slice(0, 7), label: new Intl.DateTimeFormat("en-ZA", { timeZone: "Africa/Johannesburg", year: "numeric", month: "long" }).format(date) };
  });
}
function pending(orgId, uid) {
  try {
    const raw = JSON.parse(window.localStorage.getItem(queueKey(orgId, uid)) || "[]");
    return Array.isArray(raw) ? [...new Set(raw.filter((id) => typeof id === "string" && id && !id.includes("/") && id.length <= 128))] : [];
  } catch { return []; }
}
function savePending(orgId, uid, rows) {
  try {
    if (rows.length) window.localStorage.setItem(queueKey(orgId, uid), JSON.stringify(rows));
    else window.localStorage.removeItem(queueKey(orgId, uid));
  } catch { /* Live counting remains available when local storage is full. */ }
}
async function capture(orgId, sessionId, uid) {
  const key = `${orgId}|${sessionId}|${uid}`;
  if (captures.has(key)) return captures.get(key);
  const task = (async () => {
    if (auth.currentUser?.uid !== uid) throw new Error("Sign in to record this count start.");
    const { data } = await startCall({ orgId, sessionId });
    if (data?.recorded !== true) throw new Error("The count start was not acknowledged.");
    savePending(orgId, uid, pending(orgId, uid).filter((id) => id !== sessionId));
    return data;
  })();
  captures.set(key, task);
  try { return await task; } finally { captures.delete(key); }
}
// Called once, after the live index and row-bearing count have been acknowledged.
// Never called on row edits, completion, archiving or restoring a count.
export function recordAcceptedMcCountStart(orgId, sessionId) {
  const uid = auth.currentUser?.uid;
  if (!uid || !orgId || !sessionId) return;
  const rows = pending(orgId, uid);
  if (!rows.includes(sessionId)) rows.push(sessionId);
  savePending(orgId, uid, rows);
  // A billing outage must not interrupt an already saved operational count.
  void capture(orgId, sessionId, uid).catch(() => {});
}
async function flushPending(orgId, uid) {
  let remaining = false;
  // Foreground retry only. No listener, timer or unbounded backlog processing.
  for (const sessionId of pending(orgId, uid).slice(0, 3)) {
    try { await capture(orgId, sessionId, uid); }
    catch { remaining = true; break; }
  }
  return remaining || pending(orgId, uid).length > 0;
}
export async function getMonthlyBillPreview(orgId, periodKey, refresh = false) {
  const uid = auth.currentUser?.uid;
  if (!uid) throw Object.assign(new Error("Sign in to view monthly bills."), { code: "functions/unauthenticated" });
  const key = `${orgId}|${periodKey}|${uid}|${refresh}`;
  if (requests.has(key)) return requests.get(key);
  const task = (async () => {
    const startsPending = await flushPending(orgId, uid);
    if (auth.currentUser?.uid !== uid) throw Object.assign(new Error("Sign in to continue."), { code: "functions/unauthenticated" });
    const { data } = await previewCall({ orgId, periodKey, refresh });
    if (auth.currentUser?.uid !== uid) throw Object.assign(new Error("Sign in to continue."), { code: "functions/unauthenticated" });
    if (data?.kind !== "invoice_preview" || data.orgId !== orgId || data.period?.periodKey !== periodKey || data.payable !== false) {
      throw new Error("The monthly bill preview could not be confirmed.");
    }
    return { ...data, startsPending };
  })();
  requests.set(key, task);
  try { return await task; } finally { requests.delete(key); }
}
export function previewErrorMessage(error) {
  const code = String(error?.code || "").replace(/^functions\//, "");
  if (code === "unauthenticated") return "Sign in again to view monthly bills.";
  if (code === "permission-denied") return "Only an owner or billing administrator can view monthly bills for this company.";
  if (["not-found", "unavailable", "internal", "deadline-exceeded"].includes(code)) return "Monthly billing is not available right now. Please try again or contact MatrixOS support.";
  return "This monthly bill preview needs a check. Please try again or contact MatrixOS support.";
}
